import React, { useCallback, useMemo, useState } from 'react';
import classnames from 'classnames';
import {
  DATE_FORMAT,
  DEFAULT_FORMAT,
  GREATER_OR_EQUAL,
  LESS_OR_EQUAL,
  useDateTimeService,
} from '../../../../../utils/hooks/useDateService';
import DefaultFilterInputLayout from './DefaultFilterInputLayout';
import FilterActions from './FilterActions';
import { useListContext } from '../../../../ListContext';
import { BaseFilterProps } from '../../../FilterContext';
import { FloatingPlacement } from 'framework/ui/uiContract';
import { Box, Typography } from '@mui/material';

// TODO move to MonthRangeFilter
export type MonthRangeFilterLayoutType = ReactComponent<MonthRangeFilterLayoutProps>;
interface MonthRangeFilterLayoutProps extends BaseFilterProps {
  label?: string;
  children: React.ReactElement[];
  leValue: string;
  geValue: string;
  onChangeCallback: (filterPair: Dictionary<string>) => void;
  onResetFilterCallback?: () => void;
  className?: string;
  popperPlacement?: FloatingPlacement;
}

const MonthRangeFilterLayout: MonthRangeFilterLayoutType = ({
  source,
  label,
  children,
  leValue,
  geValue,
  onChangeCallback,
  className,
  onResetFilterCallback,
  popperPlacement,
}) => {
  const { filter: listFilter = {} } = useListContext();
  const { getLocalDate } = useDateTimeService();
  const [isMenuOpened, setIsMenuOpen] = useState(false);

  const formatDate = useCallback(
    (value: string, format = DATE_FORMAT.INPUT_MONTH_VALUE) => {
      return value && getLocalDate(value).isValid() ? getLocalDate(value).format(format) : '';
    },
    [getLocalDate],
  );

  const displayedValue = useMemo(() => {
    let dateValue = '';

    if (geValue && !leValue) {
      dateValue += 'from ';
    }

    dateValue += formatDate(geValue) || '';

    if (geValue && leValue) {
      dateValue += ' - ';
    }

    if (!geValue && leValue) {
      dateValue += 'to ';
    }

    dateValue += formatDate(leValue) || '';

    return dateValue;
  }, [geValue, leValue, formatDate]);

  const dateInputProps = {
    label: '',
    showCalendar: false,
    className: 'filter-datepicker',
  };

  const dateInputs = React.Children.toArray(children) as React.ReactElement[];

  const toggleMenu = () => {
    if (isMenuOpened) {
      resetFilterChanges();
    }

    setIsMenuOpen(prevValue => !prevValue);
  };

  const resetFilterChanges = useCallback(() => {
    const previousFilterValue = listFilter[source] || {};

    onChangeCallback({
      [GREATER_OR_EQUAL]: formatDate(previousFilterValue[GREATER_OR_EQUAL], DEFAULT_FORMAT),
    });
    onChangeCallback({
      [LESS_OR_EQUAL]: formatDate(previousFilterValue[LESS_OR_EQUAL], DEFAULT_FORMAT),
    });
  }, [listFilter, onChangeCallback, formatDate, source]);

  return (
    <DefaultFilterInputLayout
      label={label}
      displayedValue={displayedValue}
      isOpen={isMenuOpened}
      toggleMenu={toggleMenu}
      source={source}
      className={classnames('daterange-button-dropdown', className)}
      onResetFilterCallback={onResetFilterCallback}
      popperPlacement={popperPlacement}
    >
      <Box
        className="daterange-inputs"
        sx={{
          display: 'flex',
          width: '100%',
          padding: '14px 16px 7px',
          '& .MuiFormControl-root': {
            width: '115px',
            margin: 0,
            '& .MuiFormHelperText-root': {
              marginLeft: 0,
              marginRight: 0,
              marginBottom: '3px',
              fontSize: '11px',
              lineHeight: 1.4,
            },
          },
          '& .MuiInputBase-root': {
            padding: 0,
            '& input': { padding: '10px 12px' },
          },
          '& > div:last-child .rdtPicker': { right: 0 },
        }}
      >
        {React.cloneElement(dateInputs[0], dateInputProps)}
        <Typography
          component="span"
          variant="inherit"
          className="daterange-separator"
          sx={{ margin: '0 16px', paddingTop: '7px' }}
        >
          —
        </Typography>
        {React.cloneElement(dateInputs[2] || dateInputs[1], dateInputProps)}
      </Box>
      <FilterActions
        onChangeCallback={() => {
          setIsMenuOpen(false);
        }}
        onResetCallback={toggleMenu}
      />
    </DefaultFilterInputLayout>
  );
};

export default MonthRangeFilterLayout;
